import { useState } from "react"

import { Decimal } from "../../classes/decimal"

export interface UseDecimalInputProps {
  decimal?: Decimal
  onChangeDecimal?: (d: Decimal) => void
  onBlurDecimal?: (d: Decimal) => void
  numDecimalPlaces?: number
  trimTrailingZeros?: boolean
  allowNegative?: boolean
  treatEmptyAsZero?: boolean
}

export function useDecimalInput({
  decimal,
  onChangeDecimal,
  onBlurDecimal,
  numDecimalPlaces,
  trimTrailingZeros = false,
  allowNegative = true,
  treatEmptyAsZero = true,
}: UseDecimalInputProps) {
  const toDecimalPlaces = (d: Decimal, decimalPlaces?: number) => {
    if (trimTrailingZeros && decimalPlaces) {
      return d.round(decimalPlaces).toString()
    }
    return d.toFixed(decimalPlaces)
  }

  const [decimalString, setDecimalString] = useState<string>(
    decimal ? decimal.toString() : ""
  )

  const [isEditing, setIsEditing] = useState(false)

  // Valid partial inputs that represent zero
  const basePartialInputs = [".", ""]
  const negativePartialInputs = ["-", "-."]
  const allPartialInputs = [...basePartialInputs, ...negativePartialInputs]

  const value =
    !isEditing && decimal
      ? numDecimalPlaces && decimal.places() < numDecimalPlaces
        ? toDecimalPlaces(decimal, numDecimalPlaces)
        : decimal.toString()
      : decimalString

  const handleChange = (value: string) => {
    const allowedPartials = allowNegative ? allPartialInputs : basePartialInputs
    if (allowedPartials.includes(value)) {
      setDecimalString(value)
      if (treatEmptyAsZero) {
        onChangeDecimal?.(Decimal.zero())
      }
      return
    }

    // Check if input is a valid string that can be converted to a decimal
    try {
      let sanitizedInput = value
      if (
        numDecimalPlaces !== undefined &&
        numDecimalPlaces < 1 &&
        value[value.length - 1] === "."
      ) {
        sanitizedInput = value.slice(0, value.indexOf("."))
      }
      // Validate negative numbers
      if (!allowNegative && sanitizedInput.startsWith("-")) {
        return
      }

      const sanitizedDecimalInput = Decimal.fromBigSource(sanitizedInput)

      if (
        numDecimalPlaces &&
        sanitizedInput.split(".")[1]?.length > numDecimalPlaces
      ) {
        return
      }

      setDecimalString(sanitizedInput)
      onChangeDecimal?.(sanitizedDecimalInput) // Update parent state if valid input
    } catch {
      // Error from fromString
    }
  }

  const handleBlur = () => {
    setIsEditing(false)
    if (allPartialInputs.includes(decimalString)) {
      if (!treatEmptyAsZero) {
        setDecimalString(decimal ? decimal.toString() : "")
        return
      }
      const zero = Decimal.zero()
      setDecimalString(zero.toString())
      onBlurDecimal?.(zero)
      return
    }
    onBlurDecimal?.(Decimal.fromBigSource(decimalString))
  }

  const handleFocus = () => {
    setIsEditing(true)
    if (decimal) {
      setDecimalString(decimal.toString())
    }
  }

  return {
    value,
    handleChange,
    handleBlur,
    handleFocus,
  }
}
